import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApp } from "@/hooks/use-app-context";
import { useConfirmedLogout } from "@/hooks/use-confirm";
import { Icon, type IconName } from "@/lib/icons";
import { contributorHeaderCopy } from "./contributor-header-copy";
import { buildRoleLinks, getDisplayName, getInitials, getRoleCaption } from "./user-menu";

interface UserMenuProps {
  lang: string;
}

interface Row {
  href: string;
  label: string;
  icon: IconName;
}

/**
 * The account menu of the contributor app: who you are, your pages, your
 * other spaces, then the website and logout. One line per entry, no labels
 * stacked over each item.
 */
export function UserMenu({ lang }: UserMenuProps) {
  const { user, t, setUser } = useApp();
  const copy = contributorHeaderCopy[lang === "en" ? "en" : "fr"];
  const base = `/${lang}`;
  const logout = useConfirmedLogout(useCallback(() => setUser(null), [setUser]));
  const roleLinks = useMemo(() => (user ? buildRoleLinks(lang, t, user) : []), [lang, t, user]);

  if (!user) return null;

  const rows: Row[] = [
    { href: `${base}/settings`, label: copy.myProfile, icon: "user" },
    { href: `${base}/my-contributions`, label: copy.myContributions, icon: "barChart3" },
    { href: `${base}/leaderboard`, label: copy.leaderboard, icon: "target" },
  ];
  // Earnings are already in the tabs; the other spaces a role opens stay here.
  const spaces = roleLinks.filter((link) => !link.href.endsWith("/get-paid"));
  const contact = (user as { email?: string; phoneNumber?: string }).email || (user as { phoneNumber?: string }).phoneNumber;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={copy.accountMenu}
          className="grid size-10 shrink-0 place-items-center rounded-full bg-foreground text-sm font-semibold text-background transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {getInitials(user)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-72 max-w-[calc(100vw-1rem)] rounded-xl border-border/60 bg-popover p-0">
        <div className="flex items-start justify-between gap-3 px-4 py-3.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{getDisplayName(user)}</p>
            {contact ? <p className="mt-0.5 truncate text-xs text-muted-foreground">{contact}</p> : null}
          </div>
          <span className="shrink-0 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand">{getRoleCaption(user, t)}</span>
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="p-1">
          {rows.map((row) => (
            <MenuLink key={row.href} row={row} />
          ))}
        </div>
        {spaces.length > 0 ? (
          <>
            <DropdownMenuSeparator className="m-0" />
            <div className="p-1">
              {spaces.map((link) => (
                <MenuLink key={link.href} row={{ href: link.href, label: link.label, icon: link.icon }} />
              ))}
            </div>
          </>
        ) : null}
        <DropdownMenuSeparator className="m-0" />
        <div className="p-1">
          <MenuLink row={{ href: `${base}/site`, label: copy.website, icon: "externalLink" }} />
          <DropdownMenuItem onClick={logout} className="cursor-pointer gap-3 rounded-md px-3 py-2 text-sm text-danger focus:text-danger">
            <Icon name="logOut" className="size-4" />
            {copy.logout}
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuLink({ row }: { row: Row }) {
  return (
    <DropdownMenuItem asChild className="cursor-pointer gap-3 rounded-md px-3 py-2 text-sm">
      <Link to={row.href}>
        <Icon name={row.icon} className="size-4 text-muted-foreground" />
        {row.label}
      </Link>
    </DropdownMenuItem>
  );
}
